import { ItemType } from '../items/types.js';
import { getWorkstationTypeFromObject, WorkstationType, } from './types.js';
const MAX_FUEL_UNITS_DEFAULT = 255;
const DEFAULT_FUEL_UNIT_BURN_DURATION_MS = 5000;
const FUEL_STATE_ENCODING_MARKER = 1 << 20;
const FUEL_STATE_BURNING_MASK = 0b1;
const FUEL_STATE_FUEL_SHIFT = 1;
// Add entries here to enable fuel behavior for additional workstation types.
export const WORKSTATION_FUEL_CONFIGS = {
    [WorkstationType.CAMPFIRE]: {
        initialFuelUnits: 4,
        maxFuelUnits: MAX_FUEL_UNITS_DEFAULT,
        fuelUnitsPerCraft: 1,
        fuelUnitBurnDurationMs: DEFAULT_FUEL_UNIT_BURN_DURATION_MS * 50,
        acceptedFuelItems: [{ itemType: ItemType.WOOD, fuelUnits: 4 }],
    },
};
export const getWorkstationFuelConfig = ({ workstationType, }) => {
    return WORKSTATION_FUEL_CONFIGS[workstationType] ?? null;
};
export const getObjectFuelConfig = ({ objectType, }) => {
    const workstationType = getWorkstationTypeFromObject({ objectType });
    if (!workstationType) {
        return null;
    }
    return getWorkstationFuelConfig({ workstationType });
};
export const isFuelPoweredWorkstation = ({ workstationType, }) => {
    return getWorkstationFuelConfig({ workstationType }) !== null;
};
export const isFuelPoweredObject = ({ objectType }) => {
    return getObjectFuelConfig({ objectType }) !== null;
};
export const clampFuelUnits = ({ fuelUnits, maxFuelUnits, }) => {
    return Math.max(0, Math.min(maxFuelUnits, Math.floor(fuelUnits)));
};
export const encodeWorkstationFuelState = ({ fuelUnits, isBurning, maxFuelUnits, }) => {
    const clampedFuelUnits = clampFuelUnits({ fuelUnits, maxFuelUnits });
    const payload = (clampedFuelUnits << FUEL_STATE_FUEL_SHIFT) | (isBurning ? 1 : 0);
    return FUEL_STATE_ENCODING_MARKER + payload;
};
export const decodeWorkstationFuelState = ({ state, maxFuelUnits, }) => {
    const normalizedState = Math.max(0, Math.floor(state));
    if (normalizedState >= FUEL_STATE_ENCODING_MARKER) {
        const payload = normalizedState - FUEL_STATE_ENCODING_MARKER;
        return {
            fuelUnits: clampFuelUnits({
                fuelUnits: payload >> FUEL_STATE_FUEL_SHIFT,
                maxFuelUnits,
            }),
            isBurning: (payload & FUEL_STATE_BURNING_MASK) === FUEL_STATE_BURNING_MASK,
            isLegacyEncoding: false,
        };
    }
    const legacyFuelUnits = clampFuelUnits({
        fuelUnits: normalizedState,
        maxFuelUnits,
    });
    return {
        fuelUnits: legacyFuelUnits,
        isBurning: legacyFuelUnits > 0,
        isLegacyEncoding: true,
    };
};
export const getWorkstationFuelUnitsFromState = ({ state, maxFuelUnits = MAX_FUEL_UNITS_DEFAULT, }) => {
    return decodeWorkstationFuelState({ state, maxFuelUnits }).fuelUnits;
};
export const isWorkstationFuelActive = ({ state, maxFuelUnits = MAX_FUEL_UNITS_DEFAULT, }) => {
    return decodeWorkstationFuelState({ state, maxFuelUnits }).isBurning;
};
export const getObjectInitialFuelUnits = ({ objectType, }) => {
    const config = getObjectFuelConfig({ objectType });
    if (!config) {
        return 0;
    }
    return clampFuelUnits({
        fuelUnits: config.initialFuelUnits,
        maxFuelUnits: config.maxFuelUnits,
    });
};
export const getObjectInitialFuelState = ({ objectType, }) => {
    const config = getObjectFuelConfig({ objectType });
    if (!config) {
        return 0;
    }
    return encodeWorkstationFuelState({
        fuelUnits: config.initialFuelUnits,
        isBurning: false,
        maxFuelUnits: config.maxFuelUnits,
    });
};
export const getFuelUnitsPerCraftForObject = ({ objectType, }) => {
    const config = getObjectFuelConfig({ objectType });
    return config?.fuelUnitsPerCraft ?? 0;
};
export const getFuelUnitsForFuelItemOnObject = ({ objectType, itemType, }) => {
    const config = getObjectFuelConfig({ objectType });
    if (!config) {
        return 0;
    }
    const fuelItem = config.acceptedFuelItems.find((item) => item.itemType === itemType);
    return fuelItem?.fuelUnits ?? 0;
};
export const getFuelUnitBurnDurationForObject = ({ objectType, }) => {
    const config = getObjectFuelConfig({ objectType });
    return config?.fuelUnitBurnDurationMs ?? 0;
};
// ============================================================================
// Campfire compatibility exports
// ============================================================================
export const CAMPFIRE_STATE_UNLIT = 0;
const CAMPFIRE_CONFIG = WORKSTATION_FUEL_CONFIGS[WorkstationType.CAMPFIRE];
const CAMPFIRE_WOOD_FUEL = CAMPFIRE_CONFIG?.acceptedFuelItems[0];
export const CAMPFIRE_FUEL_UNITS_PER_WOOD = CAMPFIRE_WOOD_FUEL?.fuelUnits ?? 4;
export const CAMPFIRE_INITIAL_FUEL_UNITS = CAMPFIRE_CONFIG?.initialFuelUnits ?? 4;
export const CAMPFIRE_FUEL_UNITS_PER_CRAFT = CAMPFIRE_CONFIG?.fuelUnitsPerCraft ?? 1;
export const CAMPFIRE_FUEL_UNIT_BURN_DURATION_MS = CAMPFIRE_CONFIG?.fuelUnitBurnDurationMs ?? DEFAULT_FUEL_UNIT_BURN_DURATION_MS;
export const isCampfireLit = ({ state }) => {
    return isWorkstationFuelActive({ state, maxFuelUnits: MAX_FUEL_UNITS_DEFAULT });
};
//# sourceMappingURL=fuel.js.map